class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  connectedCallback() {
    this.formatDiscountSummary();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.discountUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.refreshDiscountSummary();
      });
    }
  }

  disconnectedCallback() {
    this.discountUpdateUnsubscriber?.();
  }

  formatDiscountSummary() {
    this.querySelectorAll('[data-ms-cart-item]').forEach((data) => {
      const row = this.querySelector(`#CartDrawer-Item-${data.dataset.msCartItem}`);
      if (!row) return;

      const setMoney = (element, amount) => {
        if (!element) return;
        element.dataset.msMoney = amount;
        element.dataset.msCurrency = data.dataset.msCurrency;
        element.dataset.msShowCurrency = 'false';
      };

      const details = row.querySelector('.cart-item__details');
      const unitPrices = details?.querySelector('.cart-item__discounted-prices');

      if (unitPrices) {
        setMoney(unitPrices.querySelector('s'), data.dataset.msOriginalPrice);
        setMoney(unitPrices.querySelector('.cart-item__final-price'), data.dataset.msFinalPrice);
      } else {
        setMoney(details?.querySelector('.product-option'), data.dataset.msFinalPrice);
      }

      const totals = row.querySelector('.cart-item__price-wrapper');
      setMoney(totals?.querySelector('s'), data.dataset.msOriginalLinePrice);
      setMoney(totals?.querySelector('span.price'), data.dataset.msFinalLinePrice);
    });

    this.querySelectorAll('[data-ms-money]').forEach((element) => {
      if (element.dataset.msMoney === undefined || element.dataset.msMoney === '') return;

      const amount = Number(element.dataset.msMoney);
      const currency = element.dataset.msCurrency;
      if (!Number.isFinite(amount) || !currency || !/^[A-Z]{3}$/.test(currency)) return;

      try {
        const money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        }).format(amount / 100);

        element.textContent =
          (element.dataset.msNegative === 'true' ? '− ' : '') +
          money +
          (element.dataset.msShowCurrency === 'false' ? '' : ' ' + currency);
      } catch {}
    });
  }

  refreshDiscountSummary() {
    if (this.discountSummaryRefresh) return this.discountSummaryRefresh;

    this.discountSummaryRefresh = fetch(`${routes.cart_url}?section_id=cart-drawer`, {
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart summary unavailable');
        return response.text();
      })
      .then((htmlText) => {
        const documentFragment = new DOMParser().parseFromString(htmlText, 'text/html');
        const latestFooter = documentFragment.querySelector('.cart-drawer__footer');
        const currentFooter = this.querySelector('.cart-drawer__footer');

        if (latestFooter && currentFooter) {
          currentFooter.innerHTML = latestFooter.innerHTML;
        }

        this.querySelectorAll('[data-ms-item-prices]').forEach((element) => {
          const key = element.getAttribute('data-ms-item-prices');
          const latest = documentFragment.querySelector(`[data-ms-item-prices="${key}"]`);
          if (latest) element.innerHTML = latest.innerHTML;
        });

        this.formatDiscountSummary();
      })
      .catch(() => {})
      .finally(() => {
        this.discountSummaryRefresh = null;
      });

    return this.discountSummaryRefresh;
  }

  open(triggeredBy) {
    this.refreshDiscountSummary();
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);